import { useEffect, useState } from "react";
import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { formatBRL, formatDate } from "../../lib/format";
import type { BillingSnapshot } from "../../hooks/useBillingSnapshots";
import { colors, fontSize, fontWeight, radius, shadow, space } from "../../lib/tokens";

interface ConfirmSendChargeDialogProps {
  snapshot: BillingSnapshot;
  onClose: () => void;
  onSent: () => void;
}

interface TenantIdentity {
  slug: string;
  name: string;
  legal_name: string | null;
  legal_cnpj: string | null;
  billing_email: string | null;
}

type BillingType = "BOLETO" | "PIX" | "UNDEFINED";

const BILLING_TYPE_LABELS: Record<BillingType, string> = {
  UNDEFINED: "Customer escolhe (boleto, PIX ou cartão)",
  BOLETO: "Boleto bancário",
  PIX: "PIX",
};

function formatCnpj(raw: string): string {
  if (raw.length !== 14) return raw;
  return `${raw.slice(0, 2)}.${raw.slice(2, 5)}.${raw.slice(5, 8)}/${raw.slice(8, 12)}-${raw.slice(12)}`;
}

export function ConfirmSendChargeDialog({ snapshot, onClose, onSent }: ConfirmSendChargeDialogProps) {
  const [tenant, setTenant] = useState<TenantIdentity | null>(null);
  const [loadingTenant, setLoadingTenant] = useState(true);
  const [billingType, setBillingType] = useState<BillingType>("UNDEFINED");
  const [sending, setSending] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [missingFields, setMissingFields] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("tenants")
        .select("slug, name, legal_name, legal_cnpj, billing_email")
        .eq("id", snapshot.tenant_id)
        .maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        setErrorMsg("Não foi possível carregar dados do tenant.");
      } else {
        setTenant(data as TenantIdentity);
      }
      setLoadingTenant(false);
    })();
    return () => { cancelled = true; };
  }, [snapshot.tenant_id]);

  const identityMissing = tenant && (!tenant.legal_cnpj || !tenant.billing_email);

  const handleSubmit = async () => {
    setSending(true);
    setErrorMsg(null);
    setMissingFields([]);

    const { data, error } = await supabase.functions.invoke("platform-send-snapshot-charge", {
      body: { snapshotId: snapshot.id, billingType },
    });
    setSending(false);

    if (error) {
      // supabase-js wraps non-2xx as FunctionsHttpError with the raw Response in
      // error.context. supabase-js does NOT consume that body, so we can parse
      // it ourselves to surface the fn's real { error, message, missing } shape.
      const ctx = (error as { context?: Response }).context;
      let payload: { error?: string; message?: string; missing?: string[] } | null = null;
      if (ctx instanceof Response) {
        try { payload = await ctx.json(); } catch { payload = null; }
      }
      if (payload?.error === "billing_identity_missing") {
        setMissingFields(payload.missing ?? []);
        return;
      }
      if (payload?.error) {
        setErrorMsg(payload.message ? `${payload.error}: ${payload.message}` : `Erro: ${payload.error}`);
        return;
      }
      setErrorMsg(error.message || "Falha ao enviar cobrança.");
      return;
    }

    const payload = data as { error?: string; missing?: string[] } | null;
    if (payload?.error === "billing_identity_missing") {
      setMissingFields(payload.missing ?? []);
      return;
    }
    if (payload?.error) {
      setErrorMsg(`Erro: ${payload.error}`);
      return;
    }
    onSent();
    onClose();
  };

  return (
    <div style={s.backdrop}>
      <div style={s.card}>
        <h2 style={s.title}>Enviar cobrança</h2>
        <p style={s.subtitle}>
          Esta ação cria uma cobrança real no Asaas e envia ao customer. Não pode ser desfeita.
        </p>

        {loadingTenant ? (
          <div style={s.skeleton}>Carregando dados do tenant...</div>
        ) : tenant ? (
          <>
            <div style={s.section}>
              <div style={s.sectionLabel}>Para</div>
              <div style={s.recipientCard}>
                <div style={s.recipientName}>{tenant.legal_name ?? tenant.name}</div>
                {tenant.legal_cnpj ? (
                  <div style={s.recipientLine}>CNPJ {formatCnpj(tenant.legal_cnpj)}</div>
                ) : (
                  <div style={s.recipientMissing}>CNPJ não cadastrado</div>
                )}
                {tenant.billing_email ? (
                  <div style={s.recipientLine}>{tenant.billing_email}</div>
                ) : (
                  <div style={s.recipientMissing}>Email de cobrança não cadastrado</div>
                )}
              </div>
            </div>

            <div style={s.section}>
              <div style={s.sectionLabel}>Detalhes da fatura</div>
              <dl style={s.dl}>
                <dt style={s.dt}>Período</dt>
                <dd style={s.dd}>{formatDate(snapshot.period_start)} → {formatDate(snapshot.period_end)}</dd>
                <dt style={s.dt}>Valor</dt>
                <dd style={s.dd}><strong>{formatBRL(Number(snapshot.final_total))}</strong></dd>
                <dt style={s.dt}>Vencimento</dt>
                <dd style={s.dd}>{snapshot.due_date ? formatDate(snapshot.due_date) : "—"}</dd>
              </dl>
            </div>

            <div style={s.section}>
              <div style={s.sectionLabel}>Forma de pagamento</div>
              <select
                value={billingType}
                onChange={(e) => setBillingType(e.target.value as BillingType)}
                style={s.select}
                disabled={Boolean(identityMissing)}
              >
                {(Object.keys(BILLING_TYPE_LABELS) as BillingType[]).map((bt) => (
                  <option key={bt} value={bt}>{BILLING_TYPE_LABELS[bt]}</option>
                ))}
              </select>
            </div>

            {(identityMissing || missingFields.length > 0) && (
              <div style={s.warningBox}>
                <strong>Identidade de cobrança incompleta.</strong>
                <div style={{ marginTop: space.xs, fontSize: fontSize.sm }}>
                  Preencha CNPJ e Email de cobrança em{" "}
                  <Link to={`/tenants/${tenant.slug}`} style={s.warningLink} onClick={onClose}>
                    Visão · Identidade
                  </Link>
                  {" "}antes de enviar.
                </div>
              </div>
            )}

            {errorMsg && <div style={s.errorText}>{errorMsg}</div>}

            <div style={s.actions}>
              <button onClick={onClose} disabled={sending} style={s.cancelBtn}>Cancelar</button>
              <button
                onClick={handleSubmit}
                disabled={sending || Boolean(identityMissing)}
                style={{ ...s.submitBtn, opacity: identityMissing ? 0.5 : 1 }}
              >
                {sending ? "Enviando..." : "Enviar cobrança"}
              </button>
            </div>
          </>
        ) : (
          <div style={s.errorText}>{errorMsg ?? "Tenant não encontrado."}</div>
        )}
      </div>
    </div>
  );
}

const s: Record<string, CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(14, 17, 22, 0.4)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: space.lg,
    overflowY: "auto",
  },
  card: {
    background: colors.white,
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.md,
    boxShadow: shadow.lg,
    padding: space.xxl,
    width: 480,
    maxWidth: "100%",
    maxHeight: "90vh",
    overflowY: "auto",
  },
  title: {
    margin: `0 0 ${space.xs}px 0`,
    fontSize: fontSize.xl,
    fontWeight: fontWeight.semibold,
    color: colors.ink,
  },
  subtitle: {
    margin: `0 0 ${space.lg}px 0`,
    fontSize: fontSize.sm,
    color: colors.stone,
  },
  skeleton: {
    padding: space.xl,
    textAlign: "center",
    color: colors.stone,
    fontSize: fontSize.sm,
  },
  section: {
    marginBottom: space.lg,
  },
  sectionLabel: {
    fontSize: fontSize.xs,
    fontWeight: fontWeight.medium,
    color: colors.stone,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: space.sm,
  },
  recipientCard: {
    background: colors.paper,
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.sm,
    padding: space.md,
  },
  recipientName: {
    fontSize: fontSize.base,
    fontWeight: fontWeight.semibold,
    color: colors.ink,
    marginBottom: space.xs,
  },
  recipientLine: {
    fontSize: fontSize.sm,
    color: colors.stone,
    fontFamily: "JetBrains Mono, monospace",
  },
  recipientMissing: {
    fontSize: fontSize.sm,
    color: colors.error,
  },
  dl: {
    display: "grid",
    gridTemplateColumns: "120px 1fr",
    rowGap: space.xs,
    columnGap: space.md,
    margin: 0,
    fontSize: fontSize.sm,
  },
  dt: {
    color: colors.stone,
  },
  dd: {
    margin: 0,
    color: colors.ink,
  },
  select: {
    width: "100%",
    padding: space.sm,
    fontSize: fontSize.base,
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.sm,
    background: colors.white,
    color: colors.ink,
    fontFamily: "inherit",
  },
  warningBox: {
    background: colors.warningSoft,
    border: `1px solid ${colors.warning}`,
    color: colors.warning,
    borderRadius: radius.sm,
    padding: space.md,
    fontSize: fontSize.sm,
    marginBottom: space.md,
  },
  warningLink: {
    color: colors.warning,
    fontWeight: fontWeight.semibold,
    textDecoration: "underline",
  },
  errorText: {
    color: colors.error,
    fontSize: fontSize.sm,
    background: colors.errorSoft,
    padding: space.sm,
    borderRadius: radius.sm,
    marginBottom: space.md,
  },
  actions: {
    display: "flex",
    gap: space.sm,
    justifyContent: "flex-end",
    marginTop: space.lg,
  },
  cancelBtn: {
    padding: `${space.sm}px ${space.lg}px`,
    border: `1px solid ${colors.fog}`,
    background: "transparent",
    borderRadius: radius.sm,
    color: colors.ink,
    fontSize: fontSize.base,
    fontWeight: fontWeight.medium,
    cursor: "pointer",
  },
  submitBtn: {
    padding: `${space.sm}px ${space.lg}px`,
    background: colors.indigo,
    color: colors.white,
    border: "none",
    borderRadius: radius.sm,
    fontSize: fontSize.base,
    fontWeight: fontWeight.medium,
    cursor: "pointer",
  },
};
